import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ExternalLinkIcon, EyeIcon, EyeOffIcon, RefreshCwIcon } from 'lucide-react';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  StatTile,
  TrendChart,
  useDebouncedValue,
  type DataTableSort,
  type TrendChartVariant,
  type TrendOverlay,
  type TrendSeries,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { useTableSort } from '@/lib/use-table-sort';
import {
  CHART_VARIANTS,
  INTERVAL_LABELS,
  LIVE_REFRESH_MS,
  RANGES,
  TREND_WINDOWS,
  agoLabel,
  periodLabel,
  rangeWindow,
  toTrendOverlays,
  toTrendPoints,
  type RangeKey,
  type TrendWindowKey,
} from '@/lib/report-window';
import { TogglePill } from '@/components/report-bits';
import {
  PAGE_SEGMENT,
  PAGE_SORT_KEYS,
  hideItem,
  listPages,
  publicPageUrl,
  readPageSeries,
  readPageSyncStatus,
  syncPagesNow,
  unhideItem,
  type PageListRow,
  type PageSegment,
  type PageSeriesPoint,
  type PageSeriesStats,
} from '@/lib/pages';
import { Checkbox } from '@/components/ui/checkbox';
import { InfoTip, METRIC_INFO, METRIC_LABEL } from '@/components/metric-info';
import { PercentChange, PositionChange, firstSeenLabel, lastSeenLabel, placesSuffix, positionDelta, positionHint } from '@/components/page-trend';
import { pctChange, TrafficSourcesPanel } from '@/components/traffic-sources';
import { PageMoversPanel } from '@/components/page-movers';
import { STATS_INTERVAL } from '@/lib/leads';
import { useIntervalChoice } from '@/lib/use-interval-choice';

/** Pages per page in the table. */
const PAGE_SIZE = 25;

/** Busiest pages first, unless the URL says otherwise. */
const DEFAULT_SORT: DataTableSort = { key: 'clicks', direction: 'desc' };

/** GSC retains ~16 months, so the presets stop at 12 months. */
const PAGE_RANGE_KEYS: readonly RangeKey[] = ['7d', '30d', '90d', '12m'];

/** What the chart plots per bucket. */
const CHART_METRICS = {
  clicks: { label: 'Clicks', unit: 'clicks' },
  impressions: { label: 'Impressions', unit: 'impressions' },
  ctr: { label: 'CTR', unit: 'CTR' },
  position: { label: 'Avg position', unit: 'avg position' },
  sessions: { label: 'Sessions', unit: 'sessions' },
} as const;
type ChartMetric = keyof typeof CHART_METRICS;

/** CTR and position are ratios — they draw as lines when segmented. */
function isRatioMetric(metric: ChartMetric): boolean {
  return metric === 'ctr' || metric === 'position';
}

/** The chosen metric's per-bucket chart value (CTR in %, position 1dp). */
function metricValue(point: Omit<PageSeriesPoint, 'period'>, metric: ChartMetric): number {
  switch (metric) {
    case 'clicks':
      return point.clicks;
    case 'impressions':
      return point.impressions;
    case 'sessions':
      return point.sessions;
    case 'ctr':
      return point.ctr === null ? 0 : Math.round(point.ctr * 10000) / 100;
    case 'position':
      return point.avgPosition === null ? 0 : Math.round(point.avgPosition * 10) / 10;
    default: {
      const exhaustive: never = metric;
      return exhaustive;
    }
  }
}

/** "3.7%" from a 0–1 CTR fraction. */
function ctrLabel(ctr: number | null): string {
  return ctr === null ? '—' : `${(ctr * 100).toFixed(1)}%`;
}

function positionLabel(position: number | null): string {
  return position === null ? '—' : position.toFixed(1);
}

/** Compact counts for chart axes: 9.6M, 250k, 980. */
function compactCount(value: number): string {
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/** The segmented series for the chosen metric, or undefined when unsplit. */
function toMetricSegments(stats: PageSeriesStats, metric: ChartMetric): TrendSeries[] | undefined {
  if (!stats.segments) return undefined;
  const leadIn = stats.range.leadIn;
  return stats.segments.map((segment) => ({
    key: segment.key,
    label: segment.key,
    values: segment.clicks
      .map((_, index) =>
        metricValue(
          {
            clicks: segment.clicks[index] ?? 0,
            impressions: segment.impressions[index] ?? 0,
            ctr: segment.ctr[index] ?? null,
            avgPosition: segment.avgPosition[index] ?? null,
            sessions: segment.sessions[index] ?? 0,
          },
          metric,
        ),
      )
      .slice(leadIn),
  }));
}

/** Last year's values for the visible buckets, for the chosen metric. */
function toMetricYoyOverlay(stats: PageSeriesStats, metric: ChartMetric): TrendOverlay | undefined {
  if (!stats.yoy) return undefined;
  return {
    key: 'yoy',
    label: 'Same period last year',
    values: stats.yoy.map((point) => metricValue(point, metric)).slice(stats.range.leadIn),
    tone: 1,
  };
}

/** Impressions floors for narrowing the table to pages Google actually shows. */
const IMPRESSION_FLOORS = [
  { key: 'all', value: 0, label: 'All' },
  { key: '10', value: 10, label: '10+' },
  { key: '100', value: 100, label: '100+' },
  { key: '1000', value: 1000, label: '1k+' },
  { key: '10000', value: 10000, label: '10k+' },
] as const;
type ImpressionFloorKey = (typeof IMPRESSION_FLOORS)[number]['key'];

type SegmentChoice = PageSegment | 'none';
const SEGMENT_OPTIONS: readonly { key: SegmentChoice; label: string }[] = [
  { key: 'none', label: 'Total' },
  { key: PAGE_SEGMENT.template, label: 'By template' },
];

/**
 * A page row's controls: hide or unhide it from analytics, and open the live
 * page. `labeled` spells the actions out, for the phone cards.
 */
function PageActions({
  row,
  onHide,
  onUnhide,
  labeled = false,
}: {
  readonly row: PageListRow;
  readonly onHide: () => void;
  readonly onUnhide: () => void;
  readonly labeled?: boolean;
}): React.ReactElement {
  return (
    <div className={`flex items-center gap-3 ${labeled ? 'justify-start text-portal-small' : 'justify-end'}`}>
      {row.excluded ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onUnhide();
          }}
          className="inline-flex items-center gap-1 text-brand"
          aria-label={`Unhide ${row.path}`}
          title="Unhide from analytics"
        >
          <EyeIcon className="size-4" aria-hidden="true" />
          {labeled ? 'Unhide' : null}
        </button>
      ) : (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onHide();
          }}
          className="inline-flex items-center gap-1 text-portal-muted hover:text-portal-ink"
          aria-label={`Hide ${row.path} from analytics`}
          title="Hide from analytics"
        >
          <EyeOffIcon className="size-4" aria-hidden="true" />
          {labeled ? 'Hide' : null}
        </button>
      )}
      <a
        href={publicPageUrl(row.path)}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => event.stopPropagation()}
        className="inline-flex items-center gap-1 text-brand"
        aria-label={`Open ${row.path} on bestcorporateevents.com`}
      >
        <ExternalLinkIcon className="size-4" aria-hidden="true" />
        {labeled ? 'Live page' : null}
      </a>
    </div>
  );
}

/**
 * Pages analytics (admin-only): site traffic and search performance per
 * page — GA4 sessions beside Search Console clicks/impressions/position,
 * mirrored hourly into page_stats. Click a row to focus the chart on one
 * page. CTR and position are always computed from sums, never averaged.
 *
 * @returns The pages analytics page.
 */
export function PagesAnalyticsPage(): React.ReactElement {
  const [range, setRange] = useState<RangeKey>('30d');
  const [variant, setVariant] = useState<TrendChartVariant>('bar');
  const [metric, setMetric] = useState<ChartMetric>('clicks');
  const [trends, setTrends] = useState<ReadonlySet<TrendWindowKey>>(() => new Set(['short']));
  const [segment, setSegment] = useState<SegmentChoice>('none');
  const [yoy, setYoy] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const statsWindow = useMemo(() => rangeWindow(range), [range]);
  const [effectiveInterval, setIntervalChoice] = useIntervalChoice(RANGES[range].interval);
  const leadIn = Math.max(...TREND_WINDOWS[effectiveInterval].map((window) => window.buckets)) - 1;
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);
  const urlSearch = params.get('q') ?? '';
  const [search, setSearch] = useState(urlSearch);
  const debounced = useDebouncedValue(search.trim());
  useEffect(() => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (debounced) next.set('q', debounced);
        else next.delete('q');
        if (debounced !== urlSearch) next.delete('page');
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, setParams]);
  const setPage = (next: number): void => {
    setParams((previous) => {
      const nextParams = new URLSearchParams(previous);
      nextParams.set('page', String(next));
      return nextParams;
    });
  };
  const floorParam = params.get('imp');
  const impressionFloor: ImpressionFloorKey = IMPRESSION_FLOORS.some((option) => option.key === floorParam)
    ? (floorParam as ImpressionFloorKey)
    : 'all';
  const minImpressions = IMPRESSION_FLOORS.find((option) => option.key === impressionFloor)!.value;
  const setImpressionFloor = (next: ImpressionFloorKey): void => {
    setParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        if (next === 'all') nextParams.delete('imp');
        else nextParams.set('imp', next);
        nextParams.delete('page');
        return nextParams;
      },
      { replace: true },
    );
  };
  const { sort, onSort } = useTableSort(PAGE_SORT_KEYS, DEFAULT_SORT);

  const stats = useQuery({
    queryKey: ['page-series', statsWindow.from, statsWindow.to, effectiveInterval, leadIn, segment, yoy, showHidden],
    queryFn: ({ signal }) =>
      readPageSeries(
        {
          from: statsWindow.from,
          to: statsWindow.to,
          interval: effectiveInterval,
          leadIn,
          ...(segment !== 'none' ? { segment } : {}),
          ...(yoy ? { yoy: '1' as const } : {}),
          ...(showHidden ? { includeHidden: true } : {}),
        },
        signal,
      ),
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: keepPreviousData,
  });
  const pages = useQuery({
    queryKey: ['pages', statsWindow.from, statsWindow.to, debounced, minImpressions, page, sort, showHidden],
    queryFn: ({ signal }) =>
      listPages(
        {
          from: statsWindow.from,
          to: statsWindow.to,
          page,
          limit: PAGE_SIZE,
          ...(debounced ? { search: debounced } : {}),
          ...(minImpressions > 0 ? { minImpressions } : {}),
          ...(sort ? { sort: sort.key, dir: sort.direction } : {}),
          ...(showHidden ? { includeHidden: true } : {}),
        },
        signal,
      ),
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: keepPreviousData,
  });
  const hidePage = useMutation({
    mutationFn: (path: string) => hideItem('page', path),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pages'] });
      void queryClient.invalidateQueries({ queryKey: ['page-series'] });
      void queryClient.invalidateQueries({ queryKey: ['page-movers'] });
    },
  });
  const unhidePage = useMutation({
    mutationFn: (path: string) => unhideItem('page', path),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pages'] });
      void queryClient.invalidateQueries({ queryKey: ['page-series'] });
      void queryClient.invalidateQueries({ queryKey: ['page-movers'] });
    },
  });
  const sync = useQuery({
    queryKey: ['page-sync-status'],
    queryFn: ({ signal }) => readPageSyncStatus(signal),
    refetchInterval: LIVE_REFRESH_MS,
  });
  const syncNow = useMutation({
    mutationFn: syncPagesNow,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['page-series'] });
      void queryClient.invalidateQueries({ queryKey: ['pages'] });
      void queryClient.invalidateQueries({ queryKey: ['page-sync-status'] });
    },
  });

  if (stats.isPending) return <LoadingState label="Loading page analytics…" />;
  if (stats.isError) {
    return <ErrorNotice title="Page analytics unavailable" message={errorMessage(stats.error)} />;
  }

  const data = stats.data;
  const totals = data.totals;
  const positionMoved = positionDelta(totals.avgPosition, totals.previous.avgPosition);
  const lastRun = sync.data?.lastRun ?? null;
  const syncHealthy = lastRun !== null && lastRun.error === null;
  const rangeLabel = RANGES[range].label;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ANALYTICS"
        title="Pages"
        subtitle="Search performance and traffic for every page on bestcorporateevents.com — Search Console clicks and position beside GA4 sessions, pulled hourly."
      />

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        {/* min-w-0 lets the control group shrink below its natural width on a
            phone so each SegmentedControl scrolls internally instead of pushing
            the page wider than the screen. */}
        <div className="flex min-w-0 flex-wrap gap-3">
          <SegmentedControl<RangeKey>
            label="Range"
            value={range}
            onChange={(next) => {
              setRange(next);
            }}
            options={PAGE_RANGE_KEYS.map((key) => ({ key, label: RANGES[key].label, shortLabel: RANGES[key].shortLabel }))}
          />
          <SegmentedControl
            label="Interval"
            value={effectiveInterval}
            onChange={setIntervalChoice}
            options={(Object.keys(INTERVAL_LABELS) as (keyof typeof INTERVAL_LABELS)[]).map((key) => ({
              key,
              label: INTERVAL_LABELS[key],
            }))}
          />
          {/* Hidden pages are out of every number on this screen unless this is on. */}
          <label className="flex items-center gap-2 whitespace-nowrap text-portal-small text-portal-ink">
            <Checkbox checked={showHidden} onCheckedChange={(checked) => setShowHidden(checked === true)} />
            Include hidden pages
          </label>
        </div>
        <div className="flex items-center gap-3 text-portal-micro text-portal-muted">
          {sync.data ? (
            <span className="flex items-center gap-2">
              <Pill tone={syncHealthy ? 'success' : lastRun === null ? 'neutral' : 'danger'}>
                {syncHealthy ? 'Live' : lastRun === null ? 'Not synced yet' : 'Sync degraded'}
              </Pill>
              <span>
                {sync.data.pages.toLocaleString()} pages · synced {agoLabel(lastRun?.finishedAt ?? null)}
              </span>
            </span>
          ) : null}
          <Button
            variant="secondary"
            onClick={() => syncNow.mutate()}
            disabled={syncNow.isPending}
            aria-label="Pull page stats from Google now"
          >
            <RefreshCwIcon className={syncNow.isPending ? 'size-4 animate-spin' : 'size-4'} aria-hidden="true" />
            {syncNow.isPending ? 'Syncing…' : 'Sync now'}
          </Button>
        </div>
      </div>
      {syncNow.isError ? (
        <div className="mb-4">
          <ErrorNotice title="Sync failed" message={errorMessage(syncNow.error)} />
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
        <StatTile
          label="Clicks"
          onSelect={() => setMetric('clicks')}
          selected={metric === 'clicks'}
          labelAccessory={<InfoTip label="clicks" text={METRIC_INFO.clicks} />}
          value={totals.clicks.toLocaleString()}
          changePct={totals.changePct}
          hint={`vs ${totals.previous.clicks.toLocaleString()} the previous ${rangeLabel}`}
        />
        <StatTile
          label="Impressions"
          onSelect={() => setMetric('impressions')}
          selected={metric === 'impressions'}
          labelAccessory={<InfoTip label="impressions" text={METRIC_INFO.impressions} />}
          value={compactCount(totals.impressions)}
          changePct={pctChange(totals.impressions, totals.previous.impressions)}
          hint={`vs ${compactCount(totals.previous.impressions)} the previous ${rangeLabel}`}
        />
        <StatTile
          label="CTR"
          onSelect={() => setMetric('ctr')}
          selected={metric === 'ctr'}
          labelAccessory={<InfoTip label="CTR" text={METRIC_INFO.ctr} />}
          value={ctrLabel(totals.ctr)}
          hint={`vs ${ctrLabel(totals.previous.ctr)} the previous ${rangeLabel}`}
        />
        <StatTile
          label="Avg position"
          onSelect={() => setMetric('position')}
          selected={metric === 'position'}
          labelAccessory={<InfoTip label="average position" text={METRIC_INFO.position} />}
          value={positionLabel(totals.avgPosition)}
          changePct={positionMoved}
          changeSuffix={placesSuffix(positionMoved)}
          downIsGood
          hint={positionHint(totals.avgPosition, totals.previous.avgPosition)}
        />
        <StatTile
          label="Sessions"
          onSelect={() => setMetric('sessions')}
          selected={metric === 'sessions'}
          labelAccessory={<InfoTip label="sessions" text={METRIC_INFO.sessions} />}
          value={totals.sessions.toLocaleString()}
          changePct={pctChange(totals.sessions, totals.previous.sessions)}
          hint={`vs ${totals.previous.sessions.toLocaleString()} the previous ${rangeLabel}`}
        />
      </div>

      <Panel className="mt-4 p-5">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-portal-card-title font-bold text-portal-ink">Site-wide over time</h2>
            <p className="text-portal-micro text-portal-muted">
              {INTERVAL_LABELS[effectiveInterval]} · {periodLabel(data.range.from, STATS_INTERVAL.day)} –{' '}
              {periodLabel(data.range.to, STATS_INTERVAL.day)} (calendar days, UTC)
            </p>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <SegmentedControl<ChartMetric>
              label="Metric"
              value={metric}
              onChange={setMetric}
              options={(Object.keys(CHART_METRICS) as ChartMetric[]).map((key) => ({
                key,
                label: CHART_METRICS[key].label,
              }))}
            />
            <SegmentedControl<SegmentChoice>
              label="Split by"
              value={segment}
              onChange={setSegment}
              options={SEGMENT_OPTIONS.map((option) => ({ key: option.key, label: option.label }))}
            />
            <SegmentedControl<TrendChartVariant>
              label="Chart type"
              value={variant}
              onChange={setVariant}
              options={CHART_VARIANTS.map((option) => ({ key: option.key, label: option.label }))}
            />
            <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Trend lines">
              {TREND_WINDOWS[effectiveInterval].map((window) => (
                <TogglePill
                  key={window.key}
                  on={trends.has(window.key)}
                  onToggle={() =>
                    setTrends((previous) => {
                      const next = new Set(previous);
                      if (next.has(window.key)) next.delete(window.key);
                      else next.add(window.key);
                      return next;
                    })
                  }
                >
                  {window.label}
                </TogglePill>
              ))}
              <TogglePill on={yoy} onToggle={() => setYoy((previous) => !previous)}>
                Last year
              </TogglePill>
            </div>
          </div>
        </div>
        {totals.clicks === 0 && totals.sessions === 0 ? (
          <EmptyState
            title="No traffic in this range"
            message="Data lands after the first sync — try Sync now, or a wider range."
          />
        ) : (
          <>
            <TrendChart
              points={toTrendPoints(
                { range: data.range, series: data.series, segments: null, yoy: null },
                (point) => metricValue(point, metric),
                (point) => {
                  const details: string[] = [];
                  if (metric !== 'clicks' && point.clicks > 0) details.push(`${point.clicks.toLocaleString()} clicks`);
                  if (metric !== 'ctr' && point.ctr !== null) details.push(`${ctrLabel(point.ctr)} CTR`);
                  if (metric !== 'sessions' && point.sessions > 0) {
                    details.push(`${point.sessions.toLocaleString()} sessions`);
                  }
                  return details;
                },
              )}
              series={toMetricSegments(data, metric)}
              overlays={[
                ...toTrendOverlays(
                  { range: data.range, series: data.series, segments: null, yoy: null },
                  data.series.map((point) => metricValue(point, metric)),
                  trends,
                ),
                ...(() => {
                  const overlay = toMetricYoyOverlay(data, metric);
                  return overlay ? [overlay] : [];
                })(),
              ]}
              // Ratios cannot stack: a split CTR/position chart is one line
              // per segment whichever variant is toggled.
              variant={isRatioMetric(metric) && data.segments ? 'line' : variant}
              title={CHART_METRICS[metric].label}
              unit={CHART_METRICS[metric].unit}
              formatValue={
                metric === 'ctr'
                  ? (value) => `${value}%`
                  : metric === 'position'
                    ? (value) => value.toFixed(1)
                    : compactCount
              }
            />
            <p className="mt-2 text-portal-micro text-portal-muted">
              Search data finalizes ~2 days late — the newest days firm up as Google re-reports them.
            </p>
          </>
        )}
      </Panel>

      <TrafficSourcesPanel
        hint="Where the whole site's sessions came from (GA4, live) — spot the channel or referrer a drop came from. Click into a page for its own breakdown."
        rangeLabel={RANGES[range].label}
        from={statsWindow.from}
        to={statsWindow.to}
        interval={effectiveInterval}
        includeHidden={showHidden}
      />

      <PageMoversPanel includeHidden={showHidden} />

      <div className="mt-8 mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-portal-section font-bold text-portal-ink">Pages in this range</h2>
          <p className="text-portal-micro text-portal-muted">
            Every page with traffic in the range — click a row for its full history.
          </p>
        </div>
        {/* The search floats centered between the heading and the impressions filter. */}
        <div className="flex flex-1 justify-center px-4">
          <SearchInput
            label="Search pages"
            placeholder="Path or title…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full max-w-80 min-w-56"
          />
        </div>
        <div className="flex items-center gap-3">
          <SegmentedControl<ImpressionFloorKey>
            label="Min impressions"
            value={impressionFloor}
            onChange={setImpressionFloor}
            options={IMPRESSION_FLOORS.map((option) => ({ key: option.key, label: option.label }))}
          />
        </div>
      </div>
      {pages.isError ? (
        <ErrorNotice title="Pages unavailable" message={errorMessage(pages.error)} />
      ) : pages.isPending ? (
        <LoadingState label="Loading pages…" />
      ) : pages.data.items.length === 0 ? (
        <Panel>
          <EmptyState
            title={debounced ? 'No pages match' : 'No pages in this range'}
            message={debounced ? 'Try a different search.' : 'Pages appear here after the first sync.'}
          />
        </Panel>
      ) : (
        <>
          <DataTable
            headers={[
              { label: 'Page', sortKey: 'path' },
              'Template',
              { label: 'Clicks', sortKey: 'clicks' },
              { label: 'Impressions', sortKey: 'impressions' },
              { label: 'CTR', sortKey: 'ctr' },
              { label: 'Avg position', sortKey: 'position' },
              { label: 'Sessions', sortKey: 'sessions' },
              { label: 'Leads', sortKey: 'leads', info: <InfoTip label="leads" text={METRIC_INFO.leads} /> },
              { label: METRIC_LABEL.conversionRate, sortKey: 'leadRate', info: <InfoTip label="conversion rate" text={METRIC_INFO.leadRate} /> },
              { label: 'Assisted', sortKey: 'assisted', info: <InfoTip label="assisted leads" text={METRIC_INFO.assisted} /> },
              { label: 'Assisted rate', sortKey: 'assistedRate', info: <InfoTip label="assisted rate" text={METRIC_INFO.assistedRate} /> },
              { label: METRIC_LABEL.firstSeen, sortKey: 'firstSeen', info: <InfoTip label="first seen" text={METRIC_INFO.firstSeen} /> },
              { label: METRIC_LABEL.lastSeen, sortKey: 'lastSeen', info: <InfoTip label="last seen" text={METRIC_INFO.lastSeen} /> },
              'Actions',
            ]}
            visuallyHiddenHeaders={[13]}
            rightAlignedHeaders={[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
            sort={sort}
            onSort={onSort}
            minWidth={1600}
            cards={pages.data.items.map((row) => ({
              key: row.path,
              title: (
                <>
                  {row.path}
                  {row.excluded ? <Pill tone="neutral" className="ml-2">Hidden</Pill> : null}
                </>
              ),
              subtitle: [row.title, row.template].filter(Boolean).join(' · ') || undefined,
              fields: [
                { label: 'Clicks', value: row.clicks.toLocaleString() },
                { label: 'Impressions', value: <>{compactCount(row.impressions)} <PercentChange value={row.impressionsChangePct} /></> },
                { label: 'Avg position', value: <>{positionLabel(row.avgPosition)} <PositionChange value={row.positionChange} previous={row.previous?.avgPosition ?? null} /></> },
                { label: 'Sessions', value: <>{row.sessions.toLocaleString()} <PercentChange value={row.sessionsChangePct} /></> },
                { label: METRIC_LABEL.leadsAndConversionRate, value: `${row.leads.toLocaleString()} · ${row.leadRate === null ? '—' : `${(row.leadRate * 100).toFixed(2)}%`}` },
                { label: METRIC_LABEL.firstSeen, value: firstSeenLabel(row.firstSeen, row.firstSeenAtHistoryStart) },
                { label: METRIC_LABEL.lastSeen, value: lastSeenLabel(row.lastSeen) },
              ],
              onOpen: () => navigate(`/analytics/pages/detail?path=${encodeURIComponent(row.path)}`),
              action: <PageActions row={row} onHide={() => hidePage.mutate(row.path)} onUnhide={() => unhidePage.mutate(row.path)} labeled />,
            }))}
          >
            {pages.data.items.map((row) => (
              <tr
                key={row.path}
                onClick={() => navigate(`/analytics/pages/detail?path=${encodeURIComponent(row.path)}`)}
                className={`cursor-pointer border-t border-portal-line align-middle transition-colors hover:bg-portal-bg ${row.excluded ? 'opacity-60' : ''}`}
              >
                <td className="p-3 text-portal-small">
                  <span className="block font-bold break-all text-portal-ink">
                    {row.path}
                    {row.excluded ? <Pill tone="neutral" className="ml-2">Hidden</Pill> : null}
                  </span>
                  {row.title ? (
                    <span className="block text-portal-micro text-portal-muted">{row.title}</span>
                  ) : null}
                </td>
                <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">
                  {row.template || '—'}
                </td>
                <td className="p-3 text-right text-portal-small tabular-nums">{row.clicks.toLocaleString()}</td>
                <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">
                  {row.impressions.toLocaleString()}
                </td>
                <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">
                  {ctrLabel(row.ctr)}
                </td>
                <td className="p-3 text-right text-portal-small whitespace-nowrap tabular-nums">
                  {positionLabel(row.avgPosition)}
                  {row.positionChange !== null && row.positionChange !== 0 ? (
                    <PositionChange value={row.positionChange} previous={row.previous?.avgPosition ?? null} className="ml-1 text-portal-micro" />
                  ) : null}
                </td>
                <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">
                  {row.sessions.toLocaleString()}
                </td>
                <td className="p-3 text-right text-portal-small tabular-nums font-bold text-portal-ink">
                  {row.leads.toLocaleString()}
                </td>
                <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">
                  {row.leadRate === null ? '—' : `${(row.leadRate * 100).toFixed(2)}%`}
                </td>
                <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">
                  {row.assisted.toLocaleString()}
                </td>
                <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">
                  {row.assistedRate === null ? '—' : `${(row.assistedRate * 100).toFixed(2)}%`}
                </td>
                <td className="p-3 text-right text-portal-small whitespace-nowrap text-portal-muted">
                  {firstSeenLabel(row.firstSeen, row.firstSeenAtHistoryStart)}
                </td>
                <td className="p-3 text-right text-portal-small whitespace-nowrap text-portal-muted">
                  {lastSeenLabel(row.lastSeen)}
                </td>
                <td className="p-3 text-right">
                  <PageActions row={row} onHide={() => hidePage.mutate(row.path)} onUnhide={() => unhidePage.mutate(row.path)} />
                </td>
              </tr>
            ))}
          </DataTable>
          <Pagination
            page={pages.data.pagination.page}
            totalPages={pages.data.pagination.totalPages}
            onChange={setPage}
            summary={`${(pages.data.pagination.page - 1) * PAGE_SIZE + 1}–${
              (pages.data.pagination.page - 1) * PAGE_SIZE + pages.data.items.length
            } of ${pages.data.pagination.total.toLocaleString()} pages`}
            className="mt-3"
          />
        </>
      )}
    </section>
  );
}
